import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';
import { Select, Spin } from 'antd';
import { SelectProps } from 'antd/es/select';
// Style
import styles from './SelectInfiniteScroll.module.scss';

const { Option } = Select;

export type SelectInfiniteScrollOption = {
  value: string;
  label: React.ReactNode;
};

export type LoadSelectInfiniteScrollOptionsArgs = {
  search: string;
  offset: number;
  limit: number;
};

export type LoadSelectInfiniteScrollOptionsResult = {
  options: SelectInfiniteScrollOption[];
  total: number;
};

type SelectInfiniteScrollProps = Omit<
  SelectProps<string>,
  | 'children'
  | 'options'
  | 'filterOption'
  | 'onPopupScroll'
  | 'onSearch'
  | 'showSearch'
  | 'notFoundContent'
  | 'dropdownRender'
> & {
  loadOptions: (
    args: LoadSelectInfiniteScrollOptionsArgs
  ) => Promise<LoadSelectInfiniteScrollOptionsResult>;
  /** Options always shown (e.g. currently selected item not in the loaded page). */
  extraOptions?: SelectInfiniteScrollOption[];
  pageSize?: number;
  debounceMs?: number;
  /**
   * Distance from the bottom of the dropdown (in px) at which the next page
   * is fetched. Larger values prefetch earlier.
   */
  scrollOffset?: number;
  showSearch?: boolean;
};

const SelectInfiniteScroll = ({
  loadOptions,
  extraOptions = [],
  pageSize = 40,
  debounceMs = 400,
  scrollOffset = 50,
  showSearch = true,
  ...selectProps
}: SelectInfiniteScrollProps): JSX.Element => {
  const [options, setOptions] = useState<SelectInfiniteScrollOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);
  const loadOptionsRef = useRef(loadOptions);

  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  }, [loadOptions]);

  const fetchPage = useCallback(
    async (search: string, offset: number) => {
      const requestId = ++requestIdRef.current;
      if (offset > 0) {
        loadingMoreRef.current = true;
      }
      setLoading(true);

      try {
        const result = await loadOptionsRef.current({
          search,
          offset,
          limit: pageSize,
        });

        // Ignore stale responses from an older search/page request.
        if (requestId !== requestIdRef.current) return;

        setOptions((prev) => {
          if (offset === 0) return result.options;

          const existingValues = new Set(prev.map((option) => option.value));
          return [
            ...prev,
            ...result.options.filter(
              (option) => !existingValues.has(option.value)
            ),
          ];
        });
        setHasMore(offset + result.options.length < result.total);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchPage('', 0);
  }, [fetchPage]);

  const handleSearch = useMemo(
    () =>
      debounce((search: string) => {
        setSearchTerm(search);
        setHasMore(true);
        setOptions([]);
        fetchPage(search, 0);
      }, debounceMs),
    [debounceMs, fetchPage]
  );

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const nearBottom =
      target.scrollTop + target.offsetHeight >=
      target.scrollHeight - scrollOffset;

    if (!nearBottom || !hasMore || loading || loadingMoreRef.current) {
      return;
    }

    fetchPage(searchTerm, options.length);
  };

  const mergedOptions = useMemo(() => {
    if (!extraOptions.length) return options;

    const existingValues = new Set(options.map((option) => option.value));
    const missingExtras = extraOptions.filter(
      (option) => !existingValues.has(option.value)
    );

    return missingExtras.length ? [...missingExtras, ...options] : options;
  }, [extraOptions, options]);

  return (
    <Select
      {...selectProps}
      showSearch={showSearch}
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : null}
      onSearch={showSearch ? handleSearch : undefined}
      onPopupScroll={handlePopupScroll}
      dropdownRender={(menu) => (
        <>
          {menu}
          {loading && options.length > 0 ? (
            <div className={styles.loadingMore}>
              <Spin size="small" />
            </div>
          ) : null}
        </>
      )}
    >
      {mergedOptions.map((option) => (
        <Option value={option.value} key={option.value}>
          {option.label}
        </Option>
      ))}
    </Select>
  );
};

export default SelectInfiniteScroll;
